import { Component } from '@theme/component';
import {
  formatTwoDayDeliveryDate,
  getNextCutoffDate,
  getTwoDayDeliveryDate,
  shouldShowTwoDayDelivery,
} from './two-day-delivery-date.js';

/**
 * @typedef {Object} TwoDayDeliveryRefs
 * @property {HTMLElement} date - Bold arrival date
 */

/**
 * US 2-day delivery line on the product page.
 * Stays hidden unless the market is US and Capture at Order is empty.
 *
 * @extends {Component<TwoDayDeliveryRefs>}
 */
class TwoDayDelivery extends Component {
  requiredRefs = ['date'];

  /** @type {number | undefined} */
  #cutoffTimer;

  /**
   * @param {Event} event
   */
  #onVisibilityChange = (event) => {
    if (event.target !== document) return;
    if (document.visibilityState !== 'visible') return;

    this.#render();
    this.#scheduleNextUpdate();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#scheduleNextUpdate();
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearCutoffTimer();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #render() {
    const show = shouldShowTwoDayDelivery({
      captureAtOrderPopulated: this.dataset.captureAtOrderPopulated === 'true',
      countryCode: this.dataset.country || '',
    });

    if (!show) {
      this.hidden = true;
      return;
    }

    const label = formatTwoDayDeliveryDate(getTwoDayDeliveryDate(new Date()));

    if (!label || !this.refs.date) return;

    this.refs.date.textContent = label;
    this.hidden = false;
  }

  #scheduleNextUpdate() {
    this.#clearCutoffTimer();

    const delay = getNextCutoffDate(new Date()).getTime() - Date.now();

    if (delay <= 0) return;

    this.#cutoffTimer = window.setTimeout(() => {
      this.#render();
      this.#scheduleNextUpdate();
    }, delay);
  }

  #clearCutoffTimer() {
    if (this.#cutoffTimer === undefined) return;

    window.clearTimeout(this.#cutoffTimer);
    this.#cutoffTimer = undefined;
  }
}

if (!customElements.get('two-day-delivery')) {
  customElements.define('two-day-delivery', TwoDayDelivery);
}
